import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { Button, Group, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import type { EmploymentSummary, WorkplaceSummary } from '@isg/shared-types';
import {
  useContractors,
  useHesabiKisiyeBagla,
  useHesapBaginiCoz,
  useIstihdamAc,
  useIstihdamGuncelle,
  useIstihdamKapat,
  useKisiGuncelle,
  useNakil,
  useWorkplacesTumu,
} from '../api/queries';
import { tarihGoster } from '../lib/format';
import { Rozet, SatirEylemleri, SolukSatir } from '../sozlesme';
import { YazmaHatasi } from './organizasyon-parts';

/**
 * ÇALIŞAN YAZMA YÜZEYİ — **TEK YAZMA YOLU** (TUR 9 / B1 · B2 · B3 · B5).
 *
 * ⚠️ **YAZMA KANCALARINI YALNIZ BU DOSYA ÇAĞIRIR** (künye düzeltmesi ·
 * istihdam aç/kapat/düzelt · nakil · hesap bağla/çöz). `yetki-parts` (Y1) ve
 * `organizasyon-parts` (B1) ile aynı kural, aynı gerekçe.
 *
 * ⚠️ **NAKİL YENİ BİR MEKANİZMA DEĞİLDİR** (B2 · T9-14): kişi kartında tek
 * adımdır ama mevcut iki ucu arka arkaya çağırır — kapanış `sebep=nakil` +
 * yeni istihdam. **Madde 80 korunur: geçmiş eski işyerinde kalır.**
 *
 * ⚠️ **İŞYERİ SEÇİCİSİ ARŞİVLİLERİ GÖSTERMEZ** (K12): arşivli işyerine yeni
 * istihdam açmak bugün tanımlı bir işlem değildir (madde 84 açık) —
 * çalışamayacak kontrol çizilmez (§2.40'ın ikinci seçeneği). ⚠️ Ayrılış
 * kaydı arşivli işyerinde de YAZILABİLİR ve o kapı kapatılmadı; kapanış
 * seçiciden değil, kişinin **mevcut** istihdam satırından yürür.
 */

const ANA_FIRMA = 'ana-firma';

/** K11 — künye düzeltmesi. Kimlik no/tipi ŞEMADA YOK (eşleştirmenin çapası). */
export function KunyeDuzenleyici({
  personId,
  adSoyad,
  personelSicilNo,
  email,
  onKapat,
}: {
  personId: string;
  adSoyad: string;
  personelSicilNo: string | null;
  email: string | null;
  onKapat: () => void;
}): React.JSX.Element {
  const [ad, setAd] = useState(adSoyad);
  const [sicil, setSicil] = useState(personelSicilNo ?? '');
  const [eposta, setEposta] = useState(email ?? '');
  const guncelle = useKisiGuncelle();
  return (
    <Stack gap="xs" data-testid="kunye-duzenle">
      <Group align="flex-end" gap="sm">
        <TextInput
          label="Ad soyad"
          value={ad}
          onChange={(e) => setAd(e.currentTarget.value)}
          data-testid="kunye-ad"
          w={260}
        />
        <TextInput
          label="Personel sicil no"
          value={sicil}
          onChange={(e) => setSicil(e.currentTarget.value)}
          data-testid="kunye-sicil"
          w={180}
        />
        <TextInput
          label="E-posta"
          value={eposta}
          onChange={(e) => setEposta(e.currentTarget.value)}
          data-testid="kunye-eposta"
          w={260}
        />
        <Button
          data-testid="kunye-kaydet"
          loading={guncelle.isPending}
          disabled={ad.trim() === ''}
          onClick={() =>
            guncelle.mutate(
              {
                personId,
                adSoyad: ad.trim(),
                personelSicilNo: sicil.trim() === '' ? null : sicil.trim(),
                email: eposta.trim() === '' ? null : eposta.trim(),
              },
              { onSuccess: onKapat },
            )
          }
        >
          Kaydet
        </Button>
        <Button variant="default" onClick={onKapat}>
          Vazgeç
        </Button>
      </Group>
      <Text size="xs" c="dimmed">
        Kimlik numarası ve tipi buradan değiştirilemez: üç giriş kanalının eşleştirme çapasıdır.
        Yanlış kimlikle açılmış dosya silinip yeniden açılır.
      </Text>
      <YazmaHatasi hata={guncelle.error} testId="kunye-hata" />
    </Stack>
  );
}

/**
 * İSTİHDAMLAR — liste + satır içi açma + kapatma/nakil/düzeltme.
 *
 * ⚠️ **KAPANIŞ SEBEBİ ZORUNLUDUR** ve seçenekleri **ikidir**: ayrılış ·
 * nakil. Sebebi boş kapanan kayıt ayrılış listelerinin `<> nakil`
 * süzgecinden geçer ve devir istatistiğini şişirir (Kapsam §3.3b).
 */
export function IstihdamBolumu({
  personId,
  employments,
  yazabilir,
  isverenAdi,
}: {
  personId: string;
  employments: readonly EmploymentSummary[];
  yazabilir: boolean;
  isverenAdi: (e: { companyId: string | null; contractorId: string | null }) => string;
}): React.JSX.Element {
  const [duzenlenen, setDuzenlenen] = useState<string | null>(null);
  const [kapanan, setKapanan] = useState<string | null>(null);
  const [nakledilen, setNakledilen] = useState<string | null>(null);
  // ✏️ **TUR 9 / B1 (yol testi buldu): İŞYERİ KOLONU YOKTU.** Satır yalnız
  // İŞVERENİ gösteriyordu; oysa madde 80'den beri istihdam **işyerine**
  // bağlıdır ve nakil de işyerleri arasındadır — kullanıcı *"bu istihdam
  // hangi sahada?"* sorusunu ekrandan cevaplayamıyordu.
  const isyeriAdlari = new Map((useWorkplacesTumu().data ?? []).map((w) => [w.id, w.ad] as const));
  return (
    <Stack gap="xs">
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>İşyeri</Table.Th>
            <Table.Th>İşveren</Table.Th>
            <Table.Th>Görev</Table.Th>
            <Table.Th>Başlangıç</Table.Th>
            <Table.Th>Bitiş</Table.Th>
            {yazabilir && <Table.Th />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {employments.map((e) => (
            <Table.Tr key={e.id} data-testid="istihdam-satiri">
              <Table.Td>{isyeriAdlari.get(e.workplaceId) ?? '—'}</Table.Td>
              <Table.Td>{isverenAdi(e)}</Table.Td>
              <Table.Td>{e.rol}</Table.Td>
              <Table.Td>{tarihGoster(e.baslangic)}</Table.Td>
              <Table.Td>
                {e.bitis ? (
                  <Group gap="xs">
                    <Text size="sm">{tarihGoster(e.bitis)}</Text>
                    {e.bitisSebebi === 'nakil' && (
                      <Rozet anlam="bilgi" testId="istihdam-nakil-rozeti">
                        Nakil
                      </Rozet>
                    )}
                  </Group>
                ) : (
                  'Devam ediyor'
                )}
              </Table.Td>
              {yazabilir && (
                <Table.Td>
                  <SatirEylemleri
                    testId={`istihdam-eylem-${e.id}`}
                    birincil={
                      e.bitis === null
                        ? {
                            etiket: 'Nakil',
                            onClick: () => setNakledilen(e.id),
                            testId: 'istihdam-nakil',
                          }
                        : undefined
                    }
                    digerleri={[
                      {
                        etiket: 'Rol/başlangıç düzelt',
                        onClick: () => setDuzenlenen(e.id),
                        testId: 'istihdam-duzelt',
                      },
                      {
                        etiket: 'İstihdamı kapat',
                        onClick: () => setKapanan(e.id),
                        gizli: e.bitis !== null,
                        tehlikeli: true,
                        testId: 'istihdam-kapat',
                      },
                    ]}
                  />
                </Table.Td>
              )}
            </Table.Tr>
          ))}
          {yazabilir && <IstihdamEkleSatiri personId={personId} />}
        </Table.Tbody>
      </Table>
      {duzenlenen && (
        <IstihdamDuzelt
          personId={personId}
          employment={employments.find((e) => e.id === duzenlenen)!}
          onKapat={() => setDuzenlenen(null)}
        />
      )}
      {kapanan && (
        <IstihdamKapatFormu
          personId={personId}
          employmentId={kapanan}
          onKapat={() => setKapanan(null)}
        />
      )}
      {nakledilen && (
        <NakilFormu
          personId={personId}
          employment={employments.find((e) => e.id === nakledilen)!}
          onKapat={() => setNakledilen(null)}
        />
      )}
    </Stack>
  );
}

/** İşyeri seçicisi — K12: yalnız AKTİF işyerleri (tek istekten, B4). */
function useIsyeriSecenekleri(): { value: string; label: string }[] {
  const isyerleri = useWorkplacesTumu({ durum: 'aktif' });
  return (isyerleri.data ?? []).map((w: WorkplaceSummary) => ({ value: w.id, label: w.ad }));
}

function IstihdamEkleSatiri({ personId }: { personId: string }): React.JSX.Element {
  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [isveren, setIsveren] = useState<string>(ANA_FIRMA);
  const [rol, setRol] = useState('');
  const [baslangic, setBaslangic] = useState('');
  const isyerleri = useIsyeriSecenekleri();
  const contractors = useContractors();
  const olustur = useIstihdamAc();
  const dokunuldu = workplaceId !== null || rol !== '' || baslangic !== '';
  // P12 (madde 224) — kaydedilmemiş istihdam satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'istihdam satırı');
  const kaydedilebilir = workplaceId !== null && rol.trim() !== '' && baslangic !== '';
  return (
    <SolukSatir
      testId="istihdam-ekle-satiri"
      kolonSayisi={6}
      dokunuldu={dokunuldu}
      kaydedilebilir={kaydedilebilir}
      kaydediliyor={olustur.isPending}
      onKaydet={() => {
        if (!workplaceId) return;
        olustur.mutate(
          {
            personId,
            workplaceId,
            contractorId: isveren === ANA_FIRMA ? null : isveren,
            rol: rol.trim(),
            baslangic,
          },
          {
            onSuccess: () => {
              setWorkplaceId(null);
              setIsveren(ANA_FIRMA);
              setRol('');
              setBaslangic('');
            },
          },
        );
      }}
      ikinciKatman={
        <Group gap="sm">
          <Select
            label="İşveren"
            data-testid="istihdam-ekle-isveren"
            allowDeselect={false}
            searchable
            value={isveren}
            onChange={(v) => setIsveren(v ?? ANA_FIRMA)}
            data={[
              { value: ANA_FIRMA, label: 'İşyerinin şirketi' },
              ...(contractors.data ?? []).map((c) => ({
                value: c.id,
                label: `Yüklenici — ${c.ad}`,
              })),
            ]}
            w={280}
          />
          <YazmaHatasi hata={olustur.error} testId="istihdam-ekle-hata" />
        </Group>
      }
    >
      <Table.Td colSpan={2}>
        <Select
          placeholder="İşyeri"
          data-testid="istihdam-ekle-isyeri"
          searchable
          value={workplaceId}
          onChange={setWorkplaceId}
          data={isyerleri}
          comboboxProps={{ withinPortal: true }}
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          placeholder="Görev"
          value={rol}
          onChange={(e) => setRol(e.currentTarget.value)}
          data-testid="istihdam-ekle-rol"
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          type="date"
          value={baslangic}
          onChange={(e) => setBaslangic(e.currentTarget.value)}
          data-testid="istihdam-ekle-baslangic"
        />
      </Table.Td>
      <Table.Td />
      <Table.Td />
    </SolukSatir>
  );
}

function IstihdamDuzelt({
  personId,
  employment,
  onKapat,
}: {
  personId: string;
  employment: EmploymentSummary;
  onKapat: () => void;
}): React.JSX.Element {
  const [rol, setRol] = useState(employment.rol);
  const [baslangic, setBaslangic] = useState(employment.baslangic);
  const guncelle = useIstihdamGuncelle();
  return (
    <Group align="flex-end" gap="sm" data-testid="istihdam-duzelt-formu">
      <TextInput
        label="Görev"
        value={rol}
        onChange={(e) => setRol(e.currentTarget.value)}
        data-testid="istihdam-duzelt-rol"
        w={220}
      />
      <TextInput
        label="Başlangıç"
        type="date"
        value={baslangic}
        onChange={(e) => setBaslangic(e.currentTarget.value)}
        data-testid="istihdam-duzelt-baslangic"
      />
      <Button
        data-testid="istihdam-duzelt-kaydet"
        loading={guncelle.isPending}
        disabled={rol.trim() === ''}
        onClick={() =>
          guncelle.mutate(
            { personId, employmentId: employment.id, rol: rol.trim(), baslangic },
            { onSuccess: onKapat },
          )
        }
      >
        Kaydet
      </Button>
      <Button variant="default" onClick={onKapat}>
        Vazgeç
      </Button>
      <Text size="xs" c="dimmed">
        İşyeri buradan değişmez — o bir nakildir.
      </Text>
      <YazmaHatasi hata={guncelle.error} testId="istihdam-duzelt-hata" />
    </Group>
  );
}

function IstihdamKapatFormu({
  personId,
  employmentId,
  onKapat,
}: {
  personId: string;
  employmentId: string;
  onKapat: () => void;
}): React.JSX.Element {
  const [bitis, setBitis] = useState('');
  const kapat = useIstihdamKapat();
  return (
    <Group align="flex-end" gap="sm" data-testid="istihdam-kapat-formu">
      <TextInput
        label="Ayrılış tarihi"
        type="date"
        value={bitis}
        onChange={(e) => setBitis(e.currentTarget.value)}
        data-testid="istihdam-kapat-bitis"
      />
      <Button
        data-testid="istihdam-kapat-kaydet"
        loading={kapat.isPending}
        disabled={bitis === ''}
        onClick={() =>
          kapat.mutate(
            { personId, employmentId, bitis, bitisSebebi: 'ayrilis' },
            { onSuccess: onKapat },
          )
        }
      >
        Ayrılışı kaydet
      </Button>
      <Button variant="default" onClick={onKapat}>
        Vazgeç
      </Button>
      <Text size="xs" c="dimmed">
        Ayrılış kişi dosyasını silmez; istihdam kapanır, dosya kalır.
      </Text>
      <YazmaHatasi hata={kapat.error} testId="istihdam-kapat-hata" />
    </Group>
  );
}

/**
 * NAKİL (B2 · T9-14) — **tek adım, iki uç**. Eski istihdam `nakil`
 * sebebiyle kapanır, yeni istihdam açılır; **geçmiş eski işyerinde kalır**.
 */
function NakilFormu({
  personId,
  employment,
  onKapat,
}: {
  personId: string;
  employment: EmploymentSummary;
  onKapat: () => void;
}): React.JSX.Element {
  const [tarih, setTarih] = useState('');
  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [rol, setRol] = useState(employment.rol);
  const isyerleri = useIsyeriSecenekleri().filter((s) => s.value !== employment.workplaceId);
  const nakil = useNakil();
  return (
    <Stack gap="xs" data-testid="nakil-formu">
      <Group align="flex-end" gap="sm">
        <TextInput
          label="Nakil tarihi"
          type="date"
          value={tarih}
          onChange={(e) => setTarih(e.currentTarget.value)}
          data-testid="nakil-tarih"
        />
        <Select
          label="Yeni işyeri"
          data-testid="nakil-isyeri"
          searchable
          value={workplaceId}
          onChange={setWorkplaceId}
          data={isyerleri}
          comboboxProps={{ withinPortal: true }}
          w={260}
        />
        <TextInput
          label="Yeni görev"
          value={rol}
          onChange={(e) => setRol(e.currentTarget.value)}
          data-testid="nakil-rol"
          w={220}
        />
        <Button
          data-testid="nakil-kaydet"
          loading={nakil.isPending}
          disabled={tarih === '' || workplaceId === null || rol.trim() === ''}
          onClick={() => {
            if (!workplaceId) return;
            nakil.mutate(
              {
                personId,
                employmentId: employment.id,
                bitis: tarih,
                yeni: {
                  workplaceId,
                  contractorId: employment.contractorId,
                  rol: rol.trim(),
                  baslangic: tarih,
                },
              },
              { onSuccess: onKapat },
            );
          }}
        >
          Nakli kaydet
        </Button>
        <Button variant="default" onClick={onKapat}>
          Vazgeç
        </Button>
      </Group>
      <Text size="xs" c="dimmed">
        Nakilde geçmiş eski işyerinde kalır: eski istihdam kapanır, yenisi açılır. Kişinin
        taşınabilir yetkinlikleri onunla gelir; istihdama bağlı olanlar (temel İSG eğitimi, işe
        giriş muayenesi) yeniden istenir.
      </Text>
      <YazmaHatasi hata={nakil.error} testId="nakil-hata" />
    </Stack>
  );
}

/**
 * B3 — **BEKLEYEN HESAP KUYRUĞU** (T9-21; §2.40'ın *"uyarı var, çare yok"*
 * kolu): zil *"N hesap bekliyor"* diyordu, ekran yoktu. Bağ kurma/çözme
 * §18/3 karma eşleşmenin **elle** koludur.
 *
 * ⚠️ **BAĞ ÇÖZME GERİ ALINAMAZ BİR İZ BIRAKIR:** `bag_cozuldu_at` dolar ve
 * dosya OTOMATİK eşleşmeye kapanır (e-posta devri koruması) — elle yeniden
 * bağlamak mümkündür ama alanı SIFIRLAMAZ. Bu yüzden eylem ⋮ altındadır
 * (P2-a).
 */
export function HesapBagEylemleri({
  appUserId,
  personId,
  kisiSecenekleri,
}: {
  appUserId: string;
  personId: string | null;
  kisiSecenekleri: readonly { value: string; label: string }[];
}): React.JSX.Element {
  const [secim, setSecim] = useState<string | null>(null);
  const bagla = useHesabiKisiyeBagla();
  const coz = useHesapBaginiCoz();
  if (personId) {
    return (
      <Group gap="xs" data-testid={`hesap-bag-${appUserId}`}>
        <SatirEylemleri
          testId={`hesap-bag-eylem-${appUserId}`}
          digerleri={[
            {
              etiket: 'Kişi bağını çöz',
              tehlikeli: true,
              testId: 'hesap-bag-coz',
              onClick: () => coz.mutate({ appUserId }),
            },
          ]}
        />
        <YazmaHatasi hata={coz.error} testId="hesap-bag-coz-hata" />
      </Group>
    );
  }
  return (
    <Group gap="xs" wrap="nowrap" data-testid={`hesap-bag-${appUserId}`}>
      <Select
        placeholder="Kişi seç"
        data-testid="hesap-bag-kisi"
        searchable
        value={secim}
        onChange={setSecim}
        data={[...kisiSecenekleri]}
        comboboxProps={{ withinPortal: true }}
        w={240}
      />
      <Button
        size="xs"
        data-testid="hesap-bag-kur"
        disabled={secim === null}
        loading={bagla.isPending}
        onClick={() => {
          if (!secim) return;
          bagla.mutate({ appUserId, personId: secim }, { onSuccess: () => setSecim(null) });
        }}
      >
        Bağla
      </Button>
      <YazmaHatasi hata={bagla.error} testId="hesap-bag-hata" />
    </Group>
  );
}
